import React, { useEffect, useState } from "react";
import axios from "axios";
import { AreaChart, Area, ResponsiveContainer, YAxis } from "recharts";
import { Newspaper, ArrowLeft, CheckCircle, Diamond, TrendUp, ArrowSquareOut, BookOpen, Sparkle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT = {
  emerging: { label: "Emerging", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  whitespace: { label: "💎 Whitespace", cls: "bg-violet-50 text-violet-700 ring-violet-200" },
  competitive: { label: "Competitive", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
};
const fmtVol = (v) => (v == null ? "—" : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : `${v}`);
const fmtGrowth = (g) => {
  if (g == null) return null;
  const a = Math.abs(g), s = g < 0 ? "−" : "+";
  if (a >= 9900) return `${s}99×+`;
  if (a >= 1000) return `${s}${Math.round(a / 100)}×`;
  return `${s}${Math.round(a)}%`;
};

const Spark = ({ data }) => {
  const series = (data || []).map((v, i) => (typeof v === "object" ? v : { v }));
  if (series.length < 2) return null;
  return (
    <ResponsiveContainer width="100%" height={40}>
      <AreaChart data={series} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs><linearGradient id="wr-spark" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.3} /><stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} /></linearGradient></defs>
        <YAxis hide domain={[0, "dataMax"]} />
        <Area type="monotone" dataKey="v" stroke="#8b5cf6" strokeWidth={2} fill="url(#wr-spark)" isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const Section = ({ s, onNavigate }) => {
  const t = s.topic;
  const vm = VERDICT[t.verdict] || VERDICT.emerging;
  return (
    <div className="border-b border-zinc-100 pb-8 mb-8 last:border-0" data-testid={`wr-section-${t.slug}`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="min-w-0">
          <h3 className="text-xl sm:text-2xl font-extrabold text-zinc-900 capitalize">{t.name}</h3>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-1 ${vm.cls}`}>{vm.label}</span>
            {s.meta_trend && (
              <button onClick={() => onNavigate && onNavigate("trending")} className="text-[11px] text-violet-600 hover:text-violet-800 font-medium" data-testid={`wr-meta-${t.slug}`}>
                part of {s.meta_trend.label} →
              </button>
            )}
          </div>
        </div>
        <div className="hidden sm:block w-24 flex-shrink-0">
          <Spark data={t.series} />
          <div className="text-center text-xs font-bold text-violet-600 mt-0.5">{fmtGrowth(t.growth) || "—"}</div>
        </div>
        <div className="w-16 flex-shrink-0 text-right">
          <div className="text-sm font-bold text-zinc-900">{fmtVol(t.search_volume)}</div>
          <div className="text-[10px] text-zinc-400">searches/mo</div>
        </div>
      </div>

      <p className="text-sm text-zinc-700 leading-relaxed mb-4">{s.blurb}</p>

      <div className="grid sm:grid-cols-2 gap-3">
        {/* Who's building this */}
        <div className="rounded-xl bg-zinc-50 border border-zinc-100 p-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Who's building this</div>
          {s.builders.length === 0 ? (
            <div className="text-xs text-zinc-500">No funded startup clearly owns this yet — open lane.</div>
          ) : (
            <div className="space-y-1.5">
              {s.builders.map((b) => (
                <a key={b.name} href={b.website || "#"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group">
                  {b.logo_url ? <img src={b.logo_url} alt="" className="w-5 h-5 rounded object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="w-5 h-5 rounded bg-violet-100 text-violet-600 text-[10px] font-bold flex items-center justify-center">{(b.name || "?")[0]}</div>}
                  <span className="text-xs font-semibold text-zinc-700 group-hover:text-violet-700 truncate">{b.name}</span>
                  {b.funding_total_usd ? <span className="text-[10px] text-zinc-400">${b.funding_total_usd >= 1e6 ? Math.round(b.funding_total_usd / 1e6) + "M" : Math.round(b.funding_total_usd / 1e3) + "K"}</span> : null}
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Indie-proven revenue */}
        <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2">💰 Indie-proven {s.revenue_proof.badge ? `· ${s.revenue_proof.badge}` : ""}</div>
          {s.revenue_proof.comps.length === 0 ? (
            <div className="text-xs text-zinc-500">No matched indie earner yet.</div>
          ) : (
            <div className="space-y-1.5">
              {s.revenue_proof.comps.map((c) => (
                <div key={c.name} className="flex items-center justify-between gap-2">
                  <a href={c.profile_url || "#"} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-zinc-700 hover:text-emerald-700 truncate">{c.name}</a>
                  <span className="text-[11px] font-bold text-emerald-700 flex-shrink-0">${c.monthly_usd >= 1e3 ? Math.round(c.monthly_usd / 1e3) + "k" : Math.round(c.monthly_usd)}/mo</span>
                </div>
              ))}
              <div className="text-[10px] text-zinc-400 pt-0.5">self-reported · via TrustMRR</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Detail = ({ report, onBack, onNavigate }) => (
  <div className="max-w-3xl mx-auto" data-testid="wr-detail">
    <button onClick={onBack} data-testid="wr-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-500 hover:text-zinc-900 mb-5"><ArrowLeft size={16} /> Reports Library</button>
    <div className="text-xs font-bold tracking-widest text-violet-600 mb-1">{report.date}</div>
    <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-3">{report.title}</h1>
    <p className="text-zinc-600 text-sm leading-relaxed mb-6 pb-6 border-b border-zinc-200">{report.intro}</p>
    {report.sections.map((s) => <Section key={s.topic.slug} s={s} onNavigate={onNavigate} />)}
    <div className="rounded-2xl bg-zinc-900 text-white p-6 text-center mt-4">
      <p className="text-sm text-zinc-300 mb-3">Every signal above is grounded in your real data — search volume, the startups building it, and real indie revenue.</p>
      <button onClick={() => onNavigate && onNavigate("copilot")} className="px-5 py-2.5 rounded-full bg-violet-600 text-white font-semibold hover:bg-violet-500 transition-colors" data-testid="wr-cta-advisor">Ask the Advisor about any of these →</button>
    </div>
  </div>
);

export default function ReportsLibrary({ onNavigate }) {
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reports/weekly`).then((r) => setReports(r.data.reports || [])).finally(() => setLoading(false));
  }, []);

  const open = (rid) => {
    axios.get(`${API}/reports/weekly/${rid}`).then((r) => setSelected(r.data.report));
  };

  if (selected) return <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6"><Detail report={selected} onBack={() => setSelected(null)} onNavigate={onNavigate} /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="reports-library-view">
      <div className="text-xs font-bold tracking-widest text-violet-600 mb-2 inline-flex items-center gap-1.5"><Newspaper size={13} weight="fill" /> REPORTS LIBRARY</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2">Your weekly founder report.</h1>
      <p className="text-zinc-500 max-w-3xl text-sm">
        Every week we surface the rising business & SaaS opportunities from your data — each tied to its <b className="text-violet-700">meta trend</b>, the startups already building it, and real <b className="text-emerald-700">indie revenue</b>. Like Exploding Topics, but in your lane and every number verifiable.
      </p>

      {loading ? (
        <div className="text-zinc-400 text-sm py-16 text-center">Loading reports…</div>
      ) : reports.length === 0 ? (
        <div className="text-zinc-500 text-sm py-16 text-center">
          <BookOpen size={28} className="mx-auto text-zinc-300 mb-2" weight="duotone" />
          Your first report is generating — check back in a couple of minutes.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {reports.map((r, i) => {
            // Topics not present in the previous (older) week's report → "New this week".
            const prev = reports[i + 1];
            const prevSet = prev ? new Set((prev.highlights || []).map((x) => String(x).toLowerCase())) : null;
            const newCount = prevSet ? (r.highlights || []).filter((h) => !prevSet.has(String(h).toLowerCase())).length : 0;
            return (
            <div key={r.report_id} className={`bg-white border rounded-2xl p-5 ${i === 0 ? "border-violet-300 ring-1 ring-violet-100" : "border-zinc-200"}`} data-testid={`wr-card-${r.report_id}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="text-lg font-extrabold text-zinc-900">{r.date}</div>
                <div className="flex items-center gap-1.5">
                  {prevSet && newCount > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200" data-testid={`wr-new-count-${r.report_id}`}>
                      <Sparkle size={10} weight="fill" /> {newCount === (r.highlights || []).length ? "All new" : `${newCount} new`}
                    </span>
                  )}
                  {i === 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-600 text-white">Latest</span>}
                </div>
              </div>
              <div className="space-y-1.5 mb-4">
                {(r.highlights || []).slice(0, 4).map((h) => {
                  const isNew = prevSet && !prevSet.has(String(h).toLowerCase());
                  return (
                    <div key={h} className="flex items-center gap-1.5 text-sm text-zinc-700 capitalize">
                      <CheckCircle size={15} weight="fill" className="text-violet-500 flex-shrink-0" />
                      <span className="truncate">{h}</span>
                      {isNew && (
                        <span className="ml-auto flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-600 bg-violet-50 ring-1 ring-violet-200 rounded-full px-1.5 py-0.5" data-testid={`wr-new-badge-${r.report_id}`}>
                          <Sparkle size={9} weight="fill" /> New
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {r.n_whitespace > 0 && <div className="text-[11px] text-violet-600 font-semibold mb-3 inline-flex items-center gap-1"><Diamond size={10} weight="fill" /> {r.n_whitespace} whitespace inside</div>}
              <button onClick={() => open(r.report_id)} data-testid={`wr-read-${r.report_id}`} className="w-full px-4 py-2.5 rounded-full bg-zinc-900 text-white text-sm font-semibold hover:bg-violet-600 transition-colors">
                Read Report
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
